import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/auth-store';
import { useTeamStore } from '@/stores/team-store';
import { Check, Search } from 'lucide-react';
import {
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuItem,
  Input,
  ScrollArea,
} from '@scio3/ui';
import { analyticsTeamSwitched } from '@/lib/analytics-service';
import { clearPersistedPaths } from '@/hooks/usePersistedPath';
import { clearPersistedCache } from '@scio3/graphql-client';

export function TeamSwitcher() {
  const { t } = useTranslation();
  const company = useAuthStore((s) => s.company);
  const teams = useTeamStore((s) => s.teams);
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const setActiveTeam = useTeamStore((s) => s.setActiveTeam);
  const allowTeams = company?.feature?.allowTeams;
  const [search, setSearch] = useState('');

  // Hide if only default team or teams not enabled
  if (!allowTeams || (teams.length === 1 && teams[0]._id === '1')) {
    return null;
  }

  const activeTeam = teams.find((t) => t._id === activeTeamId);
  const displayName = activeTeam?.name === 'Default' ? t('Default') : activeTeam?.name;

  const filteredTeams = teams.filter((team) => {
    if (!search.trim()) return true;
    const name = team.name === 'Default' ? t('Default') : team.name;
    return name.toLowerCase().includes(search.trim().toLowerCase());
  });

  function handleSwitch(teamId: string) {
    analyticsTeamSwitched({ fromTeamId: activeTeamId ?? undefined, toTeamId: teamId });
    setActiveTeam(teamId);
    window.dispatchEvent(new CustomEvent('team:switched', { detail: { teamId } }));

    // Clear team-scoped persisted data before navigation:
    // 1. Persisted folder paths (sessionStorage) — prevents restoring old team's folder
    clearPersistedPaths();
    // 2. Apollo cache (IndexedDB) — prevents stale data from previous team showing briefly
    clearPersistedCache();

    // Navigate to base path (strip query params like ?id=, ?path=, ?teamId=)
    // to prevent cross-team data leakage when switching teams
    const basePath = window.location.pathname;
    window.location.href = basePath;
  }

  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuSub onOpenChange={(open) => !open && setSearch('')}>
        <DropdownMenuSubTrigger data-testid="team-switcher-trigger">
          {t('Team')} ({displayName})
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="w-72">
          <div className="flex items-center gap-2 border-b px-3 py-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('Search teams...')}
              className="h-7 border-none bg-transparent shadow-none focus-visible:ring-0"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
          <ScrollArea type="auto" viewportClassName="max-h-60" onWheel={(e) => e.stopPropagation()}>
            {filteredTeams.length === 0 ? (
              <div className="px-3 py-4 text-center text-sm text-muted-foreground">
                {t('No teams found')}
              </div>
            ) : (
              filteredTeams.map((team) => (
                <DropdownMenuItem
                  key={team._id}
                  data-testid="team-switcher-option"
                  onClick={() => handleSwitch(team._id)}
                >
                  {team._id === activeTeamId && <Check className="mr-2 h-4 w-4 shrink-0" />}
                  <span className="truncate">
                    {team.name === 'Default' ? t('Default') : team.name}
                  </span>
                </DropdownMenuItem>
              ))
            )}
          </ScrollArea>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
    </>
  );
}
